import React, { useState, useEffect } from 'react';
import { securePut, securePost } from '../../config/api';
import { SaveCancel } from '../../components/PrimaryButton';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";
import { formatAmount, formatDateTime } from "../../utils/format";
import Input from '../../components/Input';
import SnackbarNotification from '../../components/SnackbarNotification';
import BLPopup from './BLPopup';
import Breadcrumbs from '../../components/Breadcrumbs';

import noImage from '../../icons/noImageIcon.svg';
import plusIcon from '../../icons/plus.svg';
import DeleteIcon from '../../icons/trashR.svg';
import plus from '../../icons/plusP.svg';
import minus from '../../icons/minusP.svg';

const CreditNoteForm = ({
    title,
    baseFields = [],
    onSuccess,
    onCancel,
}) => {
    const [formValues, setFormValues] = useState({});
    const [listEntities, setListEntities] = useState([]);
    const [BLData, setBLData] = useState([]);
    const [isPopupVisible, setPopupVisible] = useState(false);
    const history = useHistory();
    const { t } = useTranslation();
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const tva = parseFloat(process.env.REACT_APP_TVA);

    const showSnackbar = (message, severity = 'success', manual = false) => {
        setSnackbar({ open: true, message, severity, manual });
    };

    const handleInputChange = (name, value) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const removeListEntity = (index) => {
        setListEntities((prevEntities) =>
            prevEntities.filter((_, i) => i !== index)
        );
    };

    const handleQuantityChange = (index, newQuantity) => {
        setListEntities((prevEntities) => {
            const updatedEntities = [...prevEntities];
            const entity = updatedEntities[index];

            newQuantity = Math.min(
                Math.max(newQuantity, 1),
                entity.maxQuantity
            );

            updatedEntities[index].quantity = newQuantity;
            return updatedEntities;
        });
    };

    useEffect(() => {
    }, [listEntities, BLData]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const requiredFields = [...baseFields];
        const missing = requiredFields.filter(f => f.required && !formValues[f.name]);
        if (missing.length > 0) {
            showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
            return;
        }

        const payload = {
            clientId: formValues.client,
            details: listEntities.map((entity) => ({
                deliveryNoteId: BLData?.[0]?.id,
                productId: entity.productId,
                quantity: entity.quantity,
                repoId: BLData?.[0]?.carts?.[0]?.repo.id,
                salesDiscount: entity.product?.salesDiscount,
            })),
        };


        try {
            await securePut("/creditNotes/add", payload, showSnackbar);
            onSuccess();
            history.push("/credit-notes");
        } catch (error) {
            console.error("Failed to submit credit note:", error);
        }
    };

    const selectBL = async () => {
        const { product, client } = formValues;


        if (!product || !client) {
            showSnackbar(t('placeholder.empty_fields'), 'error');
            return;
        }

        try {
            const response = await securePost("/deliveryNotes/getProductInDeliveryNotes", {
                productId: product,
                clientId: client,
            });
            setBLData(response.data);
            if (response.status === 200) {
                setPopupVisible(true);
            }
        } catch (error) {
            console.error("Error submitting data:", error);
        }
    };


    const handleImageError = (e) => {
        e.target.src = noImage;
        e.target.style.width = "25px";
        e.target.style.height = "25px";
        e.target.style.objectFit = "contain";
    };

    return (
        <div className="">
            <Helmet>
                <title>GrayFox - {t("titles.br")}</title>
            </Helmet>
            <Breadcrumbs />
            <h2 className="text-[32px] text-[#38485C] font-semibold mb-4">{title}</h2>
            <form className="space-y-6">
                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {baseFields.map((field, index) => (
                            <div key={index}>
                                <Input
                                    type={field.type || "text"}
                                    label={field.label}
                                    name={field.name}
                                    value={formValues[field.name] || ""}
                                    options={field.options || []}
                                    required={field.required}
                                    onChange={(value) => handleInputChange(field.name, value)}
                                />
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={selectBL}
                            className="bg-[#043F88] text-white w-[48px] h-[48px] rounded-md hover:bg-[#3F3456] flex items-center justify-center"
                        >
                            <img src={plusIcon} alt="Add" className="w-4 h-4" />
                            </button>
                    </div>
                </div>



                <div className="space-y-[8px] mt-6">
                    {listEntities.map((entity, index) => (
                        <div key={index} className="grid grid-cols-[25%_15%_15%_10%_15%_15%_5%] items-center py-2 px-4 rounded-lg bg-white shadow-[0px_6px_26px_rgba(98,107,125,0.07)] gap-0">
                            {/* Product Image and Name */}
                            <div className="flex items-center gap-2">
                                <div className="w-[76px] h-[76px] flex items-center justify-center shadow-[0px_6px_26px_rgba(98,107,125,0.07)] rounded-[12px] overflow-hidden">
                                    <img
                                        src={entity.product.product?.image || noImage}
                                        alt={entity.product.product?.name || ""}
                                        className="w-full h-full object-contain"
                                        onError={handleImageError}
                                    />
                                </div>
                                <div>
                                    <p className="text-[#043F88] font-semibold text-[12px]">
                                        {entity.product.product?.reference || "N/A"}
                                    </p>
                                    <p className="text-[#6B78F8] font-semibold text-[16px]">
                                        {entity.product.product?.name || ""}
                                    </p>
                                </div>
                            </div>

                            {/* BL */}
                            <div className="text-center">
                                <p className="text-[#707070] font-semibold text-[12px]">Bon de livraison</p>
                                <p className="text-[#043F88] font-bold text-lg">
                                    {entity.deliveryNoteNumber}
                                </p>
                                <p className="text-[#ACAFC6] font-medium text-[13px]">
                                    {formatDateTime(entity.createdAt)}
                                </p>
                            </div>

                            {/* Price HT */}
                            <div className="text-center">
                                <p className="text-[#707070] font-semibold text-[12px]">PRICE HT</p>
                                <p className="text-[#043F88] font-bold text-lg">
                                    {formatAmount(entity.product.unitPrice)}
                                </p>
                            </div>

                            {/* Price TTC */}
                            <div className="text-center">
                                <p className="text-[#707070] font-semibold text-[12px]">PRICE TTC</p>
                                <p className="text-[#043F88] font-bold text-lg">
                                {formatAmount(entity.product.unitPrice  * (1 + tva))}
                                </p>
                            </div>

                            {/* Quantity Controls */}
                            <div className="flex flex-col items-center">
                                <p className="text-[#707070] font-semibold text-[12px] mb-1">QUANTITY</p>
                                <div className="flex items-center rounded-[12px] border border-[#6C78F8] bg-[#6B78F858] divide-x divide-[#6C78F8]">
                                    <button
                                        type="button"
                                        onClick={() => handleQuantityChange(index, entity.quantity - 1)}
                                        disabled={entity.quantity <= 1}
                                        className={`w-8 h-8 flex items-center justify-center text-[#6C78F8] font-bold ${entity.quantity <= 1 ? 'opacity-50 cursor-not-allowed' : ''
                                            }`}
                                    >
                                        <img src={minus} alt="Minus" className="w-4 h-4" />
                                        </button>
                                    <span className="w-10 text-center font-semibold text-[#38485C] text-[14px]">
                                        {entity.quantity}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => handleQuantityChange(index, entity.quantity + 1)}
                                        disabled={entity.quantity >= (entity.maxQuantity - entity.product?.returned)}
                                        className={`w-8 h-8 flex items-center justify-center text-[#6C78F8] font-bold ${entity.quantity >= entity.maxQuantity ? 'opacity-50 cursor-not-allowed' : ''
                                            }`}
                                    >
                                        <img src={plus} alt="Plus" className="w-4 h-4" />
                                        </button>
                                </div>
                                <p className="text-[#707070] text-xs mt-1">
                                    Available: {entity.maxQuantity - entity.product?.returned}
                                </p>
                            </div>

                            {/* Total Price */}
                            <div className="text-center">
                                <p className="text-[#707070] font-semibold text-[12px]">TOTAL</p>
                                <p className="text-[#043F88] font-bold text-lg">
                                    {formatAmount(entity.quantity * (entity.product.unitPrice  * (1 + tva)))}
                                </p>
                            </div>

                            {/* Remove Button */}
                            <div className="flex justify-center">
                                <button
                                    type="button"
                                    onClick={() => removeListEntity(index)}
                                    className="w-8 h-8 flex items-center justify-center bg-[#F9FAFC] rounded-[12px] border border-[#EF466F4D]"
                                >
                                    <img src={DeleteIcon} alt="Delete" className="w-4 h-4" />
                                    </button>
                            </div>
                        </div>
                    ))}
                </div>



                <div className="flex justify-between items-center mt-6">
                    <div className="flex text-left gap-8">
                        <div>
                            <span className="text-[#231E29] text-[16px] font-medium">
                                TOTAL HT:
                            </span>
                            <span className="text-[#043F88] text-[16px] font-bold pl-2">
                                {formatAmount(listEntities.reduce((sum, entity) =>
                                    sum + (entity.quantity * (entity.product?.unitPrice || 0)), 0))}
                            </span>
                        </div>
                        <div>
                            <span className="text-[#231E29] text-[16px] font-medium">
                                TOTAL TTC:
                            </span>
                            <span className="text-[#043F88] text-[16px] font-bold pl-2">
                                {formatAmount(listEntities.reduce((sum, entity) =>
                                    sum + (entity.quantity * (entity.product?.unitPrice*(1 + tva) || 0)), 0))}
                            </span>
                        </div>
                    </div>
                    <div className="space-x-4">
                        <SaveCancel
                            onSaveClick={handleSubmit}
                            onCancelClick={onCancel}
                        />
                    </div>
                </div>
            </form>

            <SnackbarNotification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                manual={snackbar.manual}
            />

            {/* Popup Component */}
            {isPopupVisible && (
                <BLPopup
                    data={BLData}
                    product={formValues.product}
                    onChoisir={(matchingProduct, deliveryNoteNumber, createdAt) => {
                        const newEntity = {
                            product: matchingProduct,
                            deliveryNoteNumber,
                            createdAt,
                            maxQuantity: matchingProduct?.quantity || 0,
                            quantity: 1,
                            clientId: formValues.client,
                            productId: formValues.product
                        };
                        setListEntities(prev => [...prev, newEntity]);
                        setPopupVisible(false);
                    }}
                    onClose={() => setPopupVisible(false)}>
                </BLPopup>
            )}
        </div>
    );
};

export default CreditNoteForm;